import React, { useContext, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Users, Clock, Filter, TrendingUp, UserCheck } from "lucide-react";
import { format, startOfWeek, endOfWeek } from "date-fns";
import toast from "react-hot-toast";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import StatsCard from "../../components/common/StatsCard";
import { AuthContext } from "../../contexts/AuthContext";
import {
  getAttendanceRecords,
  getAttendanceSummary,
  exportAttendanceReport,
} from "../../services/attendance";
import { getCourses } from "../../services/courses";
import { AttendanceFilter } from "../../types/attendance";
import AttendanceTable from "../../components/tables/AttendanceTable";

const SecretaryAttendance: React.FC = () => {
  const authContext = useContext(AuthContext);
  const user = authContext?.user;
  const [records, setRecords] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<AttendanceFilter>({
    page: 1,
    limit: 20,
    dateFrom: format(startOfWeek(new Date()), "yyyy-MM-dd"),
    dateTo: format(endOfWeek(new Date()), "yyyy-MM-dd"),
    userType: "student",
  });

  // Fetch courses for filtering
  const { data: coursesData } = useQuery({
    queryKey: ["secretary-courses"],
    queryFn: () => getCourses({ limit: 1000 }),
    enabled: !!user?.id,
  });

  const courses = coursesData?.data || [];

  useEffect(() => {
    loadData();
  }, [filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [recordsResponse, summaryResponse] = await Promise.all([
        getAttendanceRecords(filters),
        getAttendanceSummary(filters.dateFrom),
      ]);

      setRecords(recordsResponse.data);
      setSummary(summaryResponse.data);
    } catch (error) {
      toast.error("Failed to load attendance data");
      console.error("Error loading attendance:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format: "pdf" | "excel") => {
    try {
      const response = await exportAttendanceReport({
        ...filters,
        format,
      });

      // Create download link
      const link = document.createElement("a");
      link.href = response.data.downloadUrl;
      link.download = `attendance-report-${
        format === "pdf" ? ".pdf" : ".xlsx"
      }`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`Report exported successfully`);
    } catch (error) {
      toast.error("Failed to export report");
    }
  };

  const getDashboardStats = () => {
    if (!summary) return [];

    // Calculate accurate stats from summary data
    const presentCount = summary.present || 0;
    const lateCount = summary.late || 0;
    const totalCount = summary.total || 0;

    // Calculate attendance rate
    const attendanceRate =
      totalCount > 0
        ? (((presentCount + lateCount) / totalCount) * 100).toFixed(1)
        : 0;

    // Calculate students from records (unique users)
    const totalStudents =
      records.length > 0
        ? new Set(
            records.map((r) =>
              typeof r.userId === "string" ? r.userId : (r.userId as any)?._id
            )
          ).size
        : 0;

    return [
      {
        title: "Total Students",
        value: totalStudents,
        icon: <Users className="w-6 h-6" />,
        color: "bg-blue-500",
      },
      {
        title: "Avg Attendance",
        value: `${attendanceRate}%`,
        icon: <TrendingUp className="w-6 h-6" />,
        color: "bg-green-500",
      },
    ];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center p-6">
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-12">
          <div className="flex flex-col items-center justify-center gap-4">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-slate-200 border-t-blue-500"></div>
            <p className="text-slate-600 font-medium">
              Loading attendance data...
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6 space-y-6">
      {/* Header */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="bg-gradient-to-br from-blue-300 to-indigo-400 rounded-2xl p-3 shadow-lg">
              <UserCheck className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">
                Attendance Management
              </h1>
              <p className="text-slate-600 font-medium mt-1">
                Automated attendance tracking via ZKTeco biometric system
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
        <div className="flex items-center gap-2 mb-6">
          <Filter className="h-5 w-5 text-blue-500" />
          <h3 className="text-lg font-bold text-slate-900">Filters</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="space-y-2">
            <label className="text-sm font-bold text-slate-700">
              User Type
            </label>
            <select
              className="w-full px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200 rounded-xl font-bold text-slate-700 focus:border-blue-400 focus:outline-none transition-all"
              value={filters.userType || ""}
              onChange={(e) =>
                setFilters({ ...filters, userType: e.target.value as any })
              }
            >
              <option value="">All Users</option>
              <option value="student">Students</option>
              <option value="teacher">Teachers</option>
              <option value="secretary">Secretaries</option>
            </select>
          </div>

          {filters.userType === "student" && (
            <div className="space-y-2">
              <label className="text-sm font-bold text-slate-700">Course</label>
              <select
                className="w-full px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200 rounded-xl font-bold text-slate-700 focus:border-blue-400 focus:outline-none transition-all"
                value={filters.courseId || ""}
                onChange={(e) =>
                  setFilters({ ...filters, courseId: e.target.value })
                }
              >
                <option value="">All Courses</option>
                {courses.map((course: any) => (
                  <option key={course._id} value={course._id}>
                    {course.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="space-y-2">
            <label className="text-sm font-bold text-slate-700">
              From Date
            </label>
            <input
              type="date"
              className="w-full px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200 rounded-xl font-medium text-slate-900 focus:border-blue-400 focus:outline-none transition-all"
              value={filters.dateFrom || ""}
              onChange={(e) =>
                setFilters({ ...filters, dateFrom: e.target.value })
              }
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-bold text-slate-700">To Date</label>
            <input
              type="date"
              className="w-full px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200 rounded-xl font-medium text-slate-900 focus:border-blue-400 focus:outline-none transition-all"
              value={filters.dateTo || ""}
              onChange={(e) =>
                setFilters({ ...filters, dateTo: e.target.value })
              }
            />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center mt-6 gap-3">
          <button
            onClick={() =>
              setFilters({
                page: 1,
                limit: 20,
                dateFrom: format(startOfWeek(new Date()), "yyyy-MM-dd"),
                dateTo: format(endOfWeek(new Date()), "yyyy-MM-dd"),
                userType: "student",
              })
            }
            className="bg-white border-2 border-slate-200 px-5 py-2.5 rounded-xl font-bold shadow-sm hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2"
          >
            <Filter className="h-5 w-5 text-slate-500" />
            <span className="text-slate-700">Clear Filters</span>
          </button>
        </div>
      </div>

      {/* Attendance Table */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl overflow-hidden">
        <div className="p-6 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-purple-500" />
            <h3 className="text-lg font-bold text-slate-900">
              Attendance Records
            </h3>
          </div>
        </div>
        <div className="p-6">
          <AttendanceTable
            records={records}
            onRefresh={loadData}
            filters={filters}
            onFiltersChange={setFilters}
          />
        </div>
      </div>
    </div>
  );
};

export default SecretaryAttendance;
